import { FormattedMessage } from "react-intl";
import { useFormContext } from "react-hook-form";
import { EyeIcon, Lock01Icon } from "@/icons";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/kit/ui/form";
import { RadioGroup, RadioGroupItem } from "@/kit/ui/radio-group";
import { messages as accessMessages } from "@/pages/_app.workspaces/_utils";

interface WorkspaceAccessFieldProps {
  /// Form field name holding "PUBLIC" | "PRIVATE" (defaults to "access").
  name?: string;
  /// Selects the "only you" vs "you or your team" wording.
  viewerIsOwner: boolean;
}

/// The PUBLIC/PRIVATE visibility radio shared by the workspace create + clone
/// dialogs. Reads the form via context, so it must render inside a `<Form>`.
export default function WorkspaceAccessField({
  name = "access",
  viewerIsOwner,
}: WorkspaceAccessFieldProps) {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="w-full">
          <FormControl>
            <RadioGroup
              onValueChange={field.onChange}
              value={field.value}
              className="flex flex-col gap-3"
            >
              <FormItem className="flex flex-row items-baseline gap-3">
                <FormControl>
                  <RadioGroupItem value="PUBLIC" />
                </FormControl>
                <div className="flex flex-col">
                  <FormLabel className="flex flex-row items-center text-grey-600 font-bold gap-1">
                    <EyeIcon className="w-5" />
                    <FormattedMessage {...accessMessages.public} />
                  </FormLabel>
                  <FormDescription>
                    <FormattedMessage
                      {...(viewerIsOwner
                        ? accessMessages.publicExplanation1
                        : accessMessages.publicExplanation2)}
                    />
                  </FormDescription>
                </div>
              </FormItem>
              <FormItem className="flex flex-row items-baseline gap-3">
                <FormControl>
                  <RadioGroupItem value="PRIVATE" />
                </FormControl>
                <div className="flex flex-col">
                  <FormLabel className="flex flex-row items-center text-grey-600 font-bold gap-1">
                    <Lock01Icon className="w-5" />
                    <FormattedMessage {...accessMessages.private} />
                  </FormLabel>
                  <FormDescription>
                    <FormattedMessage
                      {...(viewerIsOwner
                        ? accessMessages.privateExplanation1
                        : accessMessages.privateExplanation2)}
                    />
                  </FormDescription>
                </div>
              </FormItem>
            </RadioGroup>
          </FormControl>
        </FormItem>
      )}
    />
  );
}
